import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, getAuthToken, setAuthToken } from "@/lib/api";
import {
  authResponseSchema,
  loginInputSchema,
  profileUpdateInputSchema,
  signupInputSchema,
  userSchema,
  type LoginInput,
  type ProfileUpdateInput,
  type SignupInput,
  type User,
} from "@/schemas";

export const meKeys = {
  current: ["me"] as const,
};

export function useMe() {
  return useQuery({
    queryKey: meKeys.current,
    enabled: !!getAuthToken(),
    queryFn: async (): Promise<User> => {
      const data = await api.get<unknown>("/me");
      return userSchema.parse(data);
    },
  });
}

export function useSignup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SignupInput) => {
      const parsed = signupInputSchema.parse(input);
      const data = await api.post<unknown>("/signup", parsed);
      return authResponseSchema.parse(data);
    },
    onSuccess: (res) => {
      setAuthToken(res.authToken);
      qc.setQueryData(meKeys.current, res.user);
    },
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: LoginInput) => {
      const parsed = loginInputSchema.parse(input);
      const data = await api.post<unknown>("/login", parsed);
      return authResponseSchema.parse(data);
    },
    onSuccess: (res) => {
      setAuthToken(res.authToken);
      qc.setQueryData(meKeys.current, res.user);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return () => {
    setAuthToken(null);
    qc.clear();
  };
}

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ProfileUpdateInput): Promise<User> => {
      const parsed = profileUpdateInputSchema.parse(input);
      const data = await api.patch<unknown>("/me", parsed);
      return userSchema.parse(data);
    },
    onSuccess: (user) => qc.setQueryData(meKeys.current, user),
  });
}
